import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Bell, BellRing, CheckCheck, AlertTriangle, Sparkles } from "lucide-react";
import { PageHeader } from "@/components/app/PageHeader";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function FeesAdminPage() {
  const { t, lang } = useI18n();
  const [fees, setFees] = useState([]);
  const [students, setStudents] = useState([]);
  const [packages, setPackages] = useState([]);
  const [config, setConfig] = useState({ email_configured: false, wabot_configured: false });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [form, setForm] = useState({
    student_id: "",
    package_id: "primary_monthly",
    name: "",
    amount: "",
    currency: "myr",
    due_date: todayISO(),
    notes: "",
  });

  const load = () => api.get("/fees").then((r) => setFees(r.data.fees));
  useEffect(() => {
    load();
    api.get("/students").then((r) => setStudents(r.data.students)).catch(() => {});
    api.get("/payments/packages").then((r) => setPackages(r.data.packages)).catch(() => {});
    api.get("/reminders/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    try {
      const payload = {
        student_id: form.student_id,
        due_date: form.due_date,
        notes: form.notes || null,
      };
      if (form.package_id && form.package_id !== "__custom__") {
        payload.package_id = form.package_id;
      } else {
        payload.name = form.name;
        payload.amount = Number(form.amount);
        payload.currency = form.currency;
      }
      await api.post("/fees", payload);
      toast.success(lang === "ms" ? "Yuran dicipta" : "Fee assigned");
      setOpen(false);
      setForm({ ...form, student_id: "", name: "", amount: "", notes: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const markPaid = async (id) => {
    if (!window.confirm(lang === "ms" ? "Tandakan sebagai dibayar?" : "Mark as paid?")) return;
    try { await api.post(`/fees/${id}/mark-paid`); load(); toast.success("Marked paid"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm(lang === "ms" ? "Padam yuran ini?" : "Delete this fee?")) return;
    try { await api.delete(`/fees/${id}`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const sendOne = async (id) => {
    setBusy(id);
    try {
      await api.post(`/reminders/send/${id}`);
      toast.success(lang === "ms" ? "Peringatan dihantar" : "Reminder sent");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const runAll = async () => {
    setBusy("all");
    try {
      const { data } = await api.post("/reminders/run");
      toast.success(`${data.processed} reminder(s) processed`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const runAutoInvoice = async () => {
    setBusy("invoice");
    try {
      const { data } = await api.post("/fees/auto-invoice/run");
      toast.success(`${data.created} ${t("invoicesCreated")} · ${data.skipped} ${t("invoicesSkipped")}`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const overdue = fees.filter((f) => f.status === "pending" && f.due_date < todayISO());

  return (
    <div className="space-y-6" data-testid="admin-fees">
      <PageHeader
        eyebrow={t("admin_dashboard_title")}
        title={t("fees")}
        subtitle={lang === "ms" ? "Tugaskan yuran bulanan kepada setiap murid." : "Assign monthly fees to each student."}
        action={
        <div className="flex flex-wrap gap-2">
          <Button
            className="gap-2 rounded-full border-2 border-yellow-400/60 bg-white/10 font-display text-xs font-extrabold uppercase tracking-wide text-yellow-300 backdrop-blur transition-colors hover:bg-white/20 hover:text-yellow-200"
            onClick={runAutoInvoice}
            disabled={busy === "invoice"}
            data-testid="auto-invoice-btn"
          >
            <Sparkles className="h-4 w-4" />
            {t("runAutoInvoice")}
          </Button>
          <Button
            className="gap-2 rounded-full border-2 border-blue-300/40 bg-white/10 font-display text-xs font-extrabold uppercase tracking-wide text-blue-100 backdrop-blur transition-colors hover:bg-white/20"
            onClick={runAll}
            disabled={busy === "all" || overdue.length === 0}
            data-testid="run-reminders-btn"
          >
            <BellRing className="h-4 w-4" />
            {lang === "ms" ? "Hantar semua peringatan" : "Send all reminders"} ({overdue.length})
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2 rounded-full bg-gradient-to-r from-orange-600 to-orange-500 px-6 font-display text-xs font-extrabold uppercase tracking-wide text-white shadow-lg shadow-orange-500/40 transition-transform duration-300 hover:scale-105" data-testid="assign-fee-btn"><Plus className="h-4 w-4" /> {t("assignFee")}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t("assignFee")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>{t("student")}</Label>
                  <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                    <SelectTrigger data-testid="fee-student" className="h-11"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name} · {s.level}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("package")}</Label>
                  <Select value={form.package_id} onValueChange={(v) => setForm({ ...form, package_id: v })}>
                    <SelectTrigger data-testid="fee-package" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {packages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — MYR {Number(p.amount).toFixed(2)}</SelectItem>)}
                      <SelectItem value="__custom__">{lang === "ms" ? "Yuran khas" : "Custom fee"}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.package_id === "__custom__" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>{t("feeName")}</Label><Input data-testid="fee-name" className="h-11" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                    <div><Label>{t("amount")} (MYR)</Label><Input data-testid="fee-amount" className="h-11 num" type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
                  </div>
                )}
                <div>
                  <Label>{t("dueDate")}</Label>
                  <Input data-testid="fee-due" type="date" className="h-11 num" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
                </div>
                <Button onClick={save} data-testid="fee-save" className="h-11 w-full rounded-full bg-gradient-to-r from-orange-600 to-orange-500 font-display font-extrabold uppercase tracking-wide text-white">{t("submit")}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        }
      />

      <div className="pmm-pop rounded-[1.5rem] border border-blue-900/15 bg-gradient-to-r from-blue-950/5 to-orange-500/5 p-4 text-sm text-foreground/80 shadow-sm" data-testid="auto-invoice-info" style={{ animationDelay: "0.1s" }}>
        <div className="flex items-start gap-2">
          <Sparkles className="mt-0.5 h-4 w-4 text-orange-600" />
          <div>
            <strong className="font-display uppercase tracking-wide text-blue-950">{t("autoInvoice")}</strong> — {t("autoInvoiceInfo")}
          </div>
        </div>
      </div>

      {(!config.email_configured || !config.wabot_configured) && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="channels-warn">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4" />
            <div>
              {lang === "ms" ? "Saluran peringatan: " : "Reminder channels: "}
              <strong>Email {config.email_configured ? "✓" : "✗"}</strong> ·
              <strong> WhatsApp {config.wabot_configured ? "✓" : "✗"}</strong>
              <span className="opacity-70"> — {lang === "ms" ? "isi WABOT_* di .env untuk WhatsApp" : "set WABOT_* env vars for WhatsApp"}</span>
            </div>
          </div>
        </div>
      )}

      <Card className="pmm-pop overflow-hidden rounded-[1.5rem] border-border/60 shadow-lg" style={{ animationDelay: "0.2s" }}><CardContent className="p-0">
        <div className="divide-y divide-border/60 md:hidden" data-testid="fees-mobile-list">
          {fees.map((f) => (
            <div key={f.id} data-testid={`fee-card-${f.id}`} className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display font-extrabold text-blue-950">{f.student_name}</div>
                  <div className="text-xs font-semibold text-muted-foreground">{f.name}</div>
                </div>
                {f.status === "paid" ? (
                  <span className="shrink-0 rounded-full bg-emerald-600/10 px-2.5 py-0.5 text-[11px] font-bold text-emerald-700">paid</span>
                ) : f.is_overdue ? (
                  <span className="shrink-0 rounded-full bg-destructive/10 px-2.5 py-0.5 text-[11px] font-bold text-destructive">overdue</span>
                ) : (
                  <span className="shrink-0 rounded-full bg-orange-500/10 px-2.5 py-0.5 text-[11px] font-bold text-orange-600">pending</span>
                )}
              </div>
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="num font-display text-lg font-extrabold text-blue-950">{f.currency.toUpperCase()} {Number(f.amount).toFixed(2)}</span>
                <span className="num text-xs text-muted-foreground">{t("dueDate")}: {f.due_date}</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {f.status !== "paid" && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => sendOne(f.id)} disabled={busy === f.id} className="h-9 flex-1 gap-1 rounded-full border-blue-900/20 text-xs font-bold text-blue-950 hover:bg-blue-50">
                      <Bell className="h-3 w-3" /> Remind
                    </Button>
                    <Button size="sm" onClick={() => markPaid(f.id)} className="h-9 flex-1 gap-1 rounded-full bg-gradient-to-r from-orange-600 to-orange-500 text-xs font-bold text-white shadow-sm shadow-orange-500/30">
                      <CheckCheck className="h-3 w-3" /> Paid
                    </Button>
                  </>
                )}
                <Button size="sm" variant="ghost" onClick={() => del(f.id)} className="h-9 rounded-full px-4 text-xs text-red-500 hover:bg-red-50 hover:text-red-600">×</Button>
              </div>
            </div>
          ))}
          {fees.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">{t("noRecordsYet")}</div>}
        </div>
        <div className="hidden md:block">
        <Table>
          <TableHeader><TableRow className="bg-blue-950 hover:bg-blue-950">
            <TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">{t("student")}</TableHead>
            <TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">{t("feeName")}</TableHead>
            <TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">{t("amount")}</TableHead>
            <TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">{t("dueDate")}</TableHead>
            <TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">Status</TableHead>
            <TableHead className="w-64"></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {fees.map((f) => (
              <TableRow key={f.id} data-testid={`fee-row-${f.id}`} className="transition-colors hover:bg-orange-50">
                <TableCell className="font-semibold text-blue-950">{f.student_name}</TableCell>
                <TableCell>{f.name}</TableCell>
                <TableCell className="num font-extrabold text-blue-950">{f.currency.toUpperCase()} {Number(f.amount).toFixed(2)}</TableCell>
                <TableCell className="num">{f.due_date}</TableCell>
                <TableCell>
                  {f.status === "paid" ? (
                    <span className="rounded-full bg-emerald-600/10 px-3 py-1 text-xs font-bold text-emerald-700">paid</span>
                  ) : f.is_overdue ? (
                    <span className="rounded-full bg-destructive/10 px-3 py-1 text-xs font-bold text-destructive">overdue</span>
                  ) : (
                    <span className="rounded-full bg-orange-500/10 px-3 py-1 text-xs font-bold text-orange-600">pending</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {f.status !== "paid" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => sendOne(f.id)} disabled={busy === f.id} data-testid={`remind-${f.id}`} className="h-8 gap-1 rounded-full border-blue-900/20 text-xs font-bold text-blue-950 hover:bg-blue-50">
                          <Bell className="h-3 w-3" /> Remind
                        </Button>
                        <Button size="sm" onClick={() => markPaid(f.id)} data-testid={`markpaid-${f.id}`} className="h-8 gap-1 rounded-full bg-gradient-to-r from-orange-600 to-orange-500 text-xs font-bold text-white shadow-sm shadow-orange-500/30">
                          <CheckCheck className="h-3 w-3" /> Paid
                        </Button>
                      </>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => del(f.id)} className="h-8 rounded-full text-xs text-red-500 hover:bg-red-50 hover:text-red-600">×</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {fees.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">{t("noRecordsYet")}</TableCell></TableRow>}
          </TableBody>
        </Table>
        </div>
      </CardContent></Card>
    </div>
  );
}
